// IdleCheckInController.js — Task #462: the character speaks up after inactivity.
//
// Once the greeting ends, a /lab legend used to sit in silent idle loops
// forever. This controller keeps the avatar alive: after a stretch of GENUINE
// user inactivity (tab visible, stage clear, nobody speaking) it asks the
// ChatController to send one brief, varied, in-character check-in through the
// SAME hidden-streaming path the greeting uses (message_type "idle_checkin" —
// never a new audio path), opening with an ambient head gesture.
//
// Anti-nag contract (all enforced here, re-checked at fire time):
//   • never while the character is speaking (TTSActive / A2F frames live)
//   • never while the mic is open or the user has a draft in the input
//   • never while a picker/panel/modal/reader overlay is up
//   • never while the tab is hidden, and never before the greeting completed
//   • progressive backoff between check-ins + hard per-session cap
//   • Safari: only once audio was already granted — an idle nudge must NEVER
//     pop a permission card (enforced again in ChatController.sendIdleCheckIn)
//   • demo/filming mode (?demo=1) never gets surprise utterances
//
// Any qualifying user activity resets the quiet clock. "Quiet" is continuous:
// speech, mic, overlays and a hidden tab all RESET the clock rather than
// merely pausing it, so a check-in only ever fires after a full uninterrupted
// quiet period on a visible, idle stage.

import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";
import { LessonReactionController } from "./LessonReactionController.js";

// ── Tuning constants (Task #462) ─────────────────────────────────────────────
// First check-in after ~75s of continuous quiet; each subsequent one needs
// twice the quiet of the previous (75s → 150s → 300s), stopping for good after
// CHECKIN_MAX_PER_SESSION have actually spoken. A stepped-away user therefore
// hears at most 3 short lines spread across ~9 minutes, then silence.
export const CHECKIN_BASE_QUIET_MS = 75_000; // quiet period before 1st check-in
export const CHECKIN_BACKOFF_FACTOR = 2.0; // quiet multiplier per check-in fired
export const CHECKIN_MAX_PER_SESSION = 3; // hard cap per page load
const CHECKIN_TICK_MS = 1_000; // watcher poll cadence
const POINTERMOVE_THROTTLE_MS = 250; // pointermove is noisy — sample it
const CHECKIN_MAX_FAILURES = 2; // network/stream failures → give up for session
const CHECKIN_MAX_SKIPS = 5; // fire-time gate said "no" this many times → stop polling
//
// QA override (real-browser verification without 75s waits):
//   window.__LL_CHECKIN_TUNING = { baseQuietMs, backoffFactor, maxPerSession }
// is read live on every tick; the instance is exposed at window.__idleCheckIns
// (debugStatus() shows the live gate states).
// ─────────────────────────────────────────────────────────────────────────────

// Body classes that mean a picker/panel/overlay owns the stage (the Hall,
// legend selector, rail panels, debate mode — the list templates/index.html
// toggles via setPanelState, plus the two picker classes ModelViewer's own
// _isPickerOpen() watches).
const OVERLAY_BODY_CLASSES = [
    "hall-open",
    "selector-open",
    "bio-open",
    "bio2-open",
    "panel-open",
    "debate-mode",
    "story-open",
    "story2-open",
    "lessons-open",
    "tutor-open",
    "history-open",
    "journal-open",
    "courses-open",
];

export class IdleCheckInController {
    /**
     * @param {ChatController} chat — the live /lab ChatController
     * @param {{isAudioGranted?: () => boolean}} opts — injected helpers
     *        (isAudioGranted = the Safari greeting-grant probe; passed in to
     *        avoid an import cycle with ChatController.js)
     */
    constructor(chat, opts = {}) {
        this.chat = chat;
        this._isAudioGranted =
            typeof opts.isAudioGranted === "function"
                ? opts.isAudioGranted
                : () => true;

        this._enabled = false;
        this._timer = null;
        this._lastActivityAt = Date.now();
        this._lastPointerMoveAt = 0;
        this._count = 0; // check-ins that actually spoke this session
        this._failures = 0; // consecutive hard failures
        this._skips = 0; // consecutive fire-time skips
        this._blocked = false; // server 403 → stop for the session
        this._inFlight = false;
        this._gestureIdx = 0;

        this._onActivity = this._onActivity.bind(this);
        this._onPointerMove = this._onPointerMove.bind(this);
        this._onVisibility = this._onVisibility.bind(this);
    }

    /** Attach activity listeners and start the watcher tick. Idempotent. */
    start() {
        if (this._enabled) return;
        this._enabled = true;
        this._lastActivityAt = Date.now();

        // Capture-phase + passive: see genuine activity even when inner UI
        // handles the event, without ever affecting scrolling/gestures.
        const opts = { capture: true, passive: true };
        window.addEventListener("pointerdown", this._onActivity, opts);
        window.addEventListener("keydown", this._onActivity, opts);
        window.addEventListener("wheel", this._onActivity, opts);
        window.addEventListener("touchstart", this._onActivity, opts);
        window.addEventListener("scroll", this._onActivity, opts);
        window.addEventListener("pointermove", this._onPointerMove, opts);
        // Typing/paste in any field (chat input included) counts as activity.
        document.addEventListener("input", this._onActivity, opts);
        document.addEventListener("visibilitychange", this._onVisibility);

        this._timer = setInterval(() => this._tick(), CHECKIN_TICK_MS);

        // QA handle (real-browser tuning/verification — see header).
        try {
            window.__idleCheckIns = this;
        } catch (e) {}
    }

    /** Detach everything. Called when the session budget is spent. */
    stop() {
        if (!this._enabled) return;
        this._enabled = false;
        const opts = { capture: true };
        window.removeEventListener("pointerdown", this._onActivity, opts);
        window.removeEventListener("keydown", this._onActivity, opts);
        window.removeEventListener("wheel", this._onActivity, opts);
        window.removeEventListener("touchstart", this._onActivity, opts);
        window.removeEventListener("scroll", this._onActivity, opts);
        window.removeEventListener("pointermove", this._onPointerMove, opts);
        document.removeEventListener("input", this._onActivity, opts);
        document.removeEventListener("visibilitychange", this._onVisibility);
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
    }

    /** Reset the quiet clock (any genuine user or system activity). */
    noteActivity(_reason) {
        this._lastActivityAt = Date.now();
    }

    // ── internals ────────────────────────────────────────────────────────────

    _onActivity() {
        this.noteActivity("user");
    }

    _onPointerMove() {
        const now = Date.now();
        if (now - this._lastPointerMoveAt < POINTERMOVE_THROTTLE_MS) return;
        this._lastPointerMoveAt = now;
        this.noteActivity("pointermove");
    }

    _onVisibility() {
        // Returning to the tab starts a FRESH quiet period — never an
        // instant check-in the moment the user comes back.
        this.noteActivity("visibility");
    }

    /** Live-tunable constants (QA override wins when present). */
    _tuning() {
        const o =
            (typeof window !== "undefined" && window.__LL_CHECKIN_TUNING) || {};
        return {
            baseQuietMs: Number(o.baseQuietMs) || CHECKIN_BASE_QUIET_MS,
            backoffFactor: Number(o.backoffFactor) || CHECKIN_BACKOFF_FACTOR,
            maxPerSession:
                Number.isFinite(Number(o.maxPerSession)) && o.maxPerSession !== undefined
                    ? Number(o.maxPerSession)
                    : CHECKIN_MAX_PER_SESSION,
        };
    }

    /** Quiet period required before the NEXT check-in (progressive backoff). */
    _quietTargetMs() {
        const t = this._tuning();
        return t.baseQuietMs * Math.pow(t.backoffFactor, this._count);
    }

    /** A picker/panel/modal/reader overlay owns the stage. */
    _overlayOpen() {
        try {
            const b = document.body;
            if (!b) return true;
            for (const cls of OVERLAY_BODY_CLASSES) {
                if (b.classList.contains(cls)) return true;
            }
            // Auth modals: the new #modal-auth uses .is-open; the legacy
            // #auth-modal toggles display. modal-switch matches #modal-auth.
            const modalAuth = document.getElementById("modal-auth");
            if (modalAuth && modalAuth.classList.contains("is-open")) return true;
            const modalSwitch = document.getElementById("modal-switch");
            if (modalSwitch && modalSwitch.classList.contains("is-open"))
                return true;
            const legacyAuth = document.getElementById("auth-modal");
            if (legacyAuth && legacyAuth.style.display === "flex") return true;
            // The EPUB reading room (#right-panel carries data-real-book="1"
            // as a state flag while a real book is open).
            const reader = document.querySelector(
                '#right-panel[data-real-book="1"]',
            );
            if (reader) return true;
        } catch (e) {}
        return false;
    }

    /** Anything speaking or in flight on the audio/chat pipeline. */
    _speechOrPipelineBusy() {
        const c = this.chat;
        try {
            if (typeof window !== "undefined" && window.TTSActive) return true;
            if (
                typeof A2FLipSyncDriver?.isAnyActive === "function" &&
                A2FLipSyncDriver.isAnyActive()
            )
                return true;
            if (c && (c.isProcessing || c.isAudioPlaying)) return true;
            if (typeof c?._isSpeechActive === "function" && c._isSpeechActive())
                return true;
        } catch (e) {}
        return false;
    }

    /** Mic open (VAD capture live). */
    _micOpen() {
        return !!this.chat?.isListening;
    }

    /** The user has an unsent draft in the chat input. */
    _draftPending() {
        try {
            const el = this.chat?.chatInput;
            return !!(el && typeof el.value === "string" && el.value.trim());
        } catch (e) {
            return false;
        }
    }

    _tick() {
        if (!this._enabled) return;
        if (this._inFlight) {
            this.noteActivity("in-flight");
            return;
        }

        const t = this._tuning();
        if (
            this._count >= t.maxPerSession ||
            this._blocked ||
            this._failures >= CHECKIN_MAX_FAILURES ||
            this._skips >= CHECKIN_MAX_SKIPS
        ) {
            this.stop();
            return;
        }

        // Continuous suppression conditions RESET the quiet clock: a check-in
        // requires a full uninterrupted quiet stretch on a visible idle stage.
        if (
            document.hidden ||
            this._overlayOpen() ||
            this._speechOrPipelineBusy() ||
            this._micOpen() ||
            this._draftPending()
        ) {
            this.noteActivity("suppressed");
            return;
        }

        // The quiet clock only starts once the greeting fully resolved.
        if (!this.chat || !this.chat._greetingCompletedAt) {
            this.noteActivity("pre-greeting");
            return;
        }

        if (Date.now() - this._lastActivityAt >= this._quietTargetMs()) {
            this._fire();
        }
    }

    async _fire() {
        // Fire-time gates that aren't continuous tick conditions.
        // Demo/filming mode: a surprise utterance ruins take repeatability.
        if (typeof window !== "undefined" && window.LL_DEMO) {
            this.noteActivity("demo-mode");
            return;
        }
        const viewer = this.chat?.modelViewer;
        const characterId =
            typeof this.chat?.getCurrentCharacterId === "function"
                ? this.chat.getCurrentCharacterId()
                : null;
        if (!viewer || !viewer.model || !characterId) {
            this.noteActivity("no-character");
            return;
        }
        // Safari hard rule (also re-checked inside sendIdleCheckIn): only
        // speak when audio was ALREADY granted — never a permission card.
        if (this.chat.isSafari && !this._isAudioGranted()) {
            this.noteActivity("safari-ungranted");
            return;
        }

        this._inFlight = true;
        try {
            // Ambient head gesture first — the head bone may ride into the
            // opening of speech (face always yields to A2F); reuses the same
            // lazily-created reaction controller the museum glance uses.
            this._gesture(viewer);

            const res = await this.chat.sendIdleCheckIn(this._count);
            const status = res && res.status;
            if (status === "success" || status === "aborted") {
                // "aborted" = the visitor barged in mid-line — the check-in
                // DID speak, and the visitor is back; both spend one slot of
                // the session budget (anti-nag direction).
                this._count += 1;
                this._failures = 0;
                this._skips = 0;
            } else if (status === "blocked") {
                // Server metering said no — stop for the whole session.
                this._blocked = true;
            } else if (status === "skipped") {
                this._skips += 1;
            } else {
                this._failures += 1;
            }
        } catch (e) {
            this._failures += 1;
        } finally {
            this._inFlight = false;
            // The next quiet period starts AFTER this attempt settles.
            this.noteActivity("checkin-settled");
        }
    }

    /**
     * Subtle ambient head gesture paired with the check-in. Uses the
     * direction verbs (head-only during speech, warm primitives when idle);
     * every path stands down for A2F per the established rules. Best-effort —
     * a gesture failure must never block the spoken line.
     */
    _gesture(viewer) {
        try {
            if (!viewer.lessonReactionController) {
                viewer.lessonReactionController = new LessonReactionController(
                    viewer,
                );
            }
            const c = viewer.lessonReactionController;
            const verbs = ["consider", "tilt", "gaze_student"];
            const verb = verbs[this._gestureIdx++ % verbs.length];
            if (typeof c.direct === "function" && c.direct(verb)) return;
            if (typeof c.consider === "function") c.consider();
        } catch (e) {
            /* optional polish — never block the check-in */
        }
    }

    /** QA/debug snapshot of every gate (window.__idleCheckIns.debugStatus()). */
    debugStatus() {
        const t = this._tuning();
        return {
            enabled: this._enabled,
            count: this._count,
            blocked: this._blocked,
            failures: this._failures,
            skips: this._skips,
            inFlight: this._inFlight,
            quietForMs: Date.now() - this._lastActivityAt,
            quietTargetMs: this._quietTargetMs(),
            tuning: t,
            gates: {
                tabHidden: !!document.hidden,
                overlayOpen: this._overlayOpen(),
                speechBusy: this._speechOrPipelineBusy(),
                micOpen: this._micOpen(),
                draftPending: this._draftPending(),
                greetingDone: !!this.chat?._greetingCompletedAt,
                demoMode: !!(typeof window !== "undefined" && window.LL_DEMO),
            },
        };
    }
}
